import { Location } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  NgZone,
  OnDestroy,
} from '@angular/core';
import {
  BehaviorSubject,
  combineLatest,
  filter,
  firstValueFrom,
  Observable,
  Subject,
  switchMap,
  takeUntil,
} from 'rxjs';
import { Customers } from 'server/shared/collections/customers';
import { Customer } from 'server/shared/models/customer';
import { DispoUser } from 'server/shared/models/user';
import { observeTracker } from 'src/helpers/meteorObservable/observeTracker';
import { rxNgZoneRun } from 'src/helpers/rxNgZoneRun';
import { CustomerService } from '../../services/customer.service';
import { PermissionsService } from '../../services/permissions.service';
import { ToastService } from '../../services/toast/toast.service';
import { UserService } from '../../services/user.service';
import { SubscriptionService } from '../../shared/subscription.service';

@Component({
  selector: 'admin-shared-pool-source-settings',
  templateUrl: 'shared-pool-source-settings.html',
  styleUrls: ['shared-pool-source-settings.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SharedPoolSourceSettingsComponent implements OnDestroy {
  private destroyed$ = new Subject<void>();

  user$: Observable<DispoUser> = this.userService.readyDispo$;
  customer$: Observable<Customer> = this.customerService.readyDispoCustomer$;

  subscriptionLoading$ =
    this.subscriptionService.sharedPoolTargetCustomers.loading$.pipe(
      rxNgZoneRun(this.ngZone),
    );

  sharedPoolCustomers$: Observable<Customer[]> =
    this.subscriptionService.sharedPoolTargetCustomers.ready$.pipe(
      filter(Boolean),
      switchMap(() => {
        return combineLatest([this.customer$]).pipe(
          switchMap(([customer]) => {
            const sharedPoolWithCustomerIds =
              customer.pool?.jobsSharedWithCustomerIds || [];
            if (sharedPoolWithCustomerIds.length === 0) {
              return observeTracker(() => []);
            }
            return observeTracker(() =>
              Customers.find({
                _id: { $in: sharedPoolWithCustomerIds },
              }).fetch(),
            );
          }),
        );
      }),
      rxNgZoneRun(this.ngZone),
    );

  selectedCustomerIds$ = new BehaviorSubject<string[]>([]);
  allowUndecidedCustomerIds$ = new BehaviorSubject<string[]>([]);

  constructor(
    private ngZone: NgZone,
    private location: Location,
    private toastService: ToastService,
    private userService: UserService,
    private customerService: CustomerService,
    private subscriptionService: SubscriptionService,
    private permissionsService: PermissionsService,
  ) {
    // Check admin permissions
    this.permissionsService.isCustomerAdmin$
      .pipe(takeUntil(this.destroyed$))
      .subscribe((isCustomerAdmin) => {
        if (!isCustomerAdmin) {
          this.historyBack();
        }
      });

    // Initialize selected customer IDs from customer settings (blacklist)
    this.customer$.pipe(takeUntil(this.destroyed$)).subscribe((customer) => {
      this.selectedCustomerIds$.next(
        customer.preventSharedPoolDirectAcceptCustomerIds || [],
      );
      this.allowUndecidedCustomerIds$.next(
        customer.sharedPoolAllowUndecidedCustomerIds || [],
      );
    });
  }

  ngOnDestroy() {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  toggleCustomerSelection(customerId: string): void {
    const currentSelection = [...this.selectedCustomerIds$.value];
    const index = currentSelection.indexOf(customerId);

    if (index > -1) {
      currentSelection.splice(index, 1);
    } else {
      currentSelection.push(customerId);
    }

    this.selectedCustomerIds$.next(currentSelection);
    this.saveSharedPoolSettings(currentSelection);
  }

  isCustomerSelected(customerId: string): boolean {
    return this.selectedCustomerIds$.value.includes(customerId);
  }

  toggleAllowUndecidedSelection(customerId: string): void {
    const currentSelection = [...this.allowUndecidedCustomerIds$.value];
    const index = currentSelection.indexOf(customerId);

    if (index > -1) {
      currentSelection.splice(index, 1);
    } else {
      currentSelection.push(customerId);
    }

    this.allowUndecidedCustomerIds$.next(currentSelection);
    this.saveSharedPoolAllowUndecidedSettings(currentSelection);
  }

  isAllowUndecidedSelected(customerId: string): boolean {
    return this.allowUndecidedCustomerIds$.value.includes(customerId);
  }

  async saveSharedPoolSettings(customerIds: string[]): Promise<void> {
    const customer = await firstValueFrom(this.customer$);
    if (!customer) return;

    Meteor.call(
      'updateSharedPoolDirectBookSettings',
      customer._id,
      customerIds,
      (error: Meteor.Error) => {
        if (error) {
          this.toastService.add({
            type: 'ERROR',
            message: error.error?.toString(),
          });
          // Revert on error
          this.selectedCustomerIds$.next(
            customer.preventSharedPoolDirectAcceptCustomerIds || [],
          );
        } else {
          this.toastService.add({ type: 'SUCCESS', message: 'changesSaved' });
        }
      },
    );
  }

  async saveSharedPoolAllowUndecidedSettings(
    customerIds: string[],
  ): Promise<void> {
    const customer = await firstValueFrom(this.customer$);
    if (!customer) return;

    Meteor.call(
      'updateSharedPoolAllowUndecidedSettings',
      customer._id,
      customerIds,
      (error: Meteor.Error) => {
        if (error) {
          this.toastService.add({
            type: 'ERROR',
            message: error.error?.toString(),
          });
          this.allowUndecidedCustomerIds$.next(
            customer.sharedPoolAllowUndecidedCustomerIds || [],
          );
        } else {
          this.toastService.add({ type: 'SUCCESS', message: 'changesSaved' });
        }
      },
    );
  }

  historyBack() {
    this.location.back();
  }
}
